import styled, { css } from 'styled-components';
import { Button, Typography } from '@mui/material';
import { Done, WarningAmberRounded } from '@mui/icons-material';

import OnboardingCard from '../../../../../components/OnboardingCard';
import ContractDetails from '../../../../../components/ContractDetails';
import { subtitle2, checkoutPagesFontStyles } from '../../../../../styles/text';

import { mixins } from '../../../../../styles/breakpoints';
import colors, { getContrastSafeColor } from '../../../../../styles/colors';
import { ReactComponent as ClockIconBase } from '../../../../../assets/onboarding/clock.svg';
import { ReactComponent as BellCrossedBase } from '../../../../../assets/onboarding/bell-crossed.svg';

const StyledOnboardingCard = styled(OnboardingCard)`
  background: ${colors.base.alpha};
  padding: 0;
  margin: 0 20px;
  background-color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark1 : colors.base.alpha)};
  ${mixins.tabletMedia(css`
    max-width: 720px;
  `)}
  ${mixins.desktopMedia(css`
    max-width: 980px;
  `)}
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: min-content;
  scroll-behavior: smooth;
  background-color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark1 : colors.base.alpha)};
  background-size: cover;
  ${checkoutPagesFontStyles};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  margin: 20px 20px 24px 20px;
  min-height: 40px;

  ${mixins.tabletMedia(css`
    width: 750px;
  `)}

  ${mixins.desktopMedia(css`
    width: 980px;
  `)}
  
  ${mixins.upToTabletMedia(css`
    width: 100%;
    max-width: 450px;
    justify-content: center;
    padding: 0;
    margin: 30px 0 0 0;
  `)}
`;

const FreeTitle = styled.div`
  ${subtitle2}
  font-size: 18px;
  text-align: center;
  padding: 10px;
  color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.base.alpha : colors.base.beta)};
`;

const InfoContainer = styled.div`
  ${checkoutPagesFontStyles}
  background-color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark4 : colors.base.alpha)};
  font-size: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  border-radius: 10px;
  margin: 10px 0;
`;

const WarningContainer = styled(InfoContainer)`
  background-color: ${colors.shades.warning1};
`;

const IconProps = css`
  margin-right: 10px;
`;

const ClockIcon = styled(ClockIconBase)`
  ${IconProps}
`;

const BellCrossedIcon = styled(BellCrossedBase)`
  ${IconProps}
`;

const Step = styled.div`
  ${checkoutPagesFontStyles};
  font-size: 14px;
  letter-spacing: 0.06em;
  margin-bottom: 5px;
  font-weight: 300;
  color: ${({ $background, $isDarkTheme }) => getContrastSafeColor(
    $isDarkTheme ? colors.shades.dark1 : colors.base.alpha,
    $background || colors.shades.gamma10,
    $isDarkTheme ? colors.base.alpha : colors.base.beta,
  )};
`;

const StepTitle = styled.h1`
  ${checkoutPagesFontStyles};
  margin: 0 0 10px 0;
  font-weight: 500;
  padding-bottom: 10px;
  border-bottom: 1px solid ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark2 : colors.shades.gamma3)};
  color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.base.alpha : colors.base.beta)};
`;

const StepSubTitle = styled.div`
  ${checkoutPagesFontStyles};
  color: ${colors.shades.gamma10};
  font-size: 12px;
  margin-bottom: 22px;
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: ${({ $direction }) => $direction || 'column'};
  justify-content: space-between;
  width: 100%;
  margin: 0 0;
  ${mixins.upToTabletMedia(css`
    margin: 0 10px 20px 10px;
  `)}
`;

const AgreementContainer = styled.div`
  ${subtitle2};
  color: ${colors.base.beta};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 20px;
  margin-top: 15px;
`;

const StyledContractDetails = styled(ContractDetails)`
  padding: 15px 0 20px;
  ${checkoutPagesFontStyles};
`;

const NoPaymentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const CoachHero = styled.img`
  aspect-ratio: 4/3;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  max-height: 300px;
`;

const CoachTitle = styled.div`
  ${checkoutPagesFontStyles};
  font-weight: 600;
  font-size: 15px;
  letter-spacing: 0.04em;
  color: ${colors.shades.gamma2};
`;

const HeaderTitle = styled.div`
  ${checkoutPagesFontStyles};
  font-weight: 800;
  font-size: 20px;
  color: ${colors.base.secondary2};
`;

const LeftSideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 450px;
  min-width: 300px;
  flex: 1;
  background-color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.base.beta : colors.shades.alpha2)};
  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;
  margin-bottom: 20px;
  padding-bottom: 20px;
`;

const RightSideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  max-width: 450px;
  min-width: 300px;
  flex: 1;
  margin-right: 20px;
  margin-top: 20px;
  margin-bottom: 20px;
  background-color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark1 : colors.base.alpha)};
  ${mixins.upToTabletMedia(css`
    margin-top: 20px;
  `)}
`;

const StyledAppIcon = styled.img`
  height: 75px;
  object-fit: contain;

  display: ${({ $isDisplay }) => ($isDisplay ? 'flex' : 'none')};

  &[type="image/svg+xml"] {
    path, circle, rect, polygon {
      fill: ${({ $fillColor }) => $fillColor};
    }
  }

`;

const BillingDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 85%;
`;

const MonthlyBillingContainer = styled.div`
  width: 100%;
  padding: 30px 0 5px 0;
  border-bottom: 1px solid ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark2 : colors.shades.gamma3)};
`;

const RecurringPaymentContainer = styled(Typography)`
  font-size: 26px;
  font-weight: 600;
  color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.base.alpha : colors.base.beta)};
`;

const MonthlyPriceContainer = styled.div`
  display: flex;
  gap: ${({ $addGap }) => ($addGap ? '10px;' : 0)}
`;

const MonthText = styled(Typography)`
  font-size: 26px;
  color: ${colors.shades.gamma14};
`;

const ProductName = styled(Typography)`
  font-size: 12px;
  color: ${colors.shades.dark4};
`;

const PackageInfos = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  row-gap: 10px;
  padding: 30px 0 45px 0;
  border-bottom: 1px solid ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark2 : colors.shades.gamma3)};
`;

const PackageInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${colors.shades.dark4};
  font-size: 14px;

  ${mixins.upToDesktopMedia(css`
    font-size: 14px;
  `)}
`;

const ImportantNotes = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px 0 20px 0;
`;

const ImportantNotesTitle = styled.div`
  color: ${colors.shades.secondary11};
  font-size: 12px;
  align-items: center;
  display: flex;
  gap: 5px;
`;

const StyledWarningIcon = styled(WarningAmberRounded)`
  width: 15px;
  height: 15px;
`;

const StyledDoneIcon = styled(Done)`
  width: 14px;
  height: 14px;
`;

const StyledList = styled.ul`
  padding-left: 20px;
`;

const StyledListItem = styled.li`
  color: ${colors.shades.gamma10};
  font-size: 12px;
`;

const UserDetailsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  width: fit-content;

  ${mixins.upToTabletMedia(css`
    margin-right: 10px;
  `)}
`;

const UserName = styled(Typography)`
  color: ${colors.shades.gamma10};

  ${mixins.upToTabletMedia(css`
    display: none;
  `)}
`;

const LogoutButton = styled(Button)`
  background-color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark2 : colors.shades.dark4)};
  color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark4 : colors.base.beta)};
  text-transform: none;
  width: 100px;
  height: 40px;
  border-radius: 20px;
  &:hover {
    background-color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark2 : colors.shades.dark4)};
    opacity: 0.8;
  }
`;

const DesktopView = styled.div`
  display: flex;
  flex-direction: row;
  gap: 70px;

  ${mixins.upToTabletMedia(css`
    display: none;
  `)}
`;

const MobileView = styled.div`
  display: none;
  padding: 20px;

  ${mixins.upToTabletMedia(css`
    display: block;
  `)}
`;

export {
  Container,
  StyledOnboardingCard,
  FreeTitle,
  InfoContainer,
  WarningContainer,
  ClockIcon,
  BellCrossedIcon,
  StepTitle,
  StepContainer,
  AgreementContainer,
  StyledContractDetails,
  NoPaymentContainer,
  HeaderContainer,
  CoachTitle,
  HeaderTitle,
  CoachHero,
  LeftSideWrapper,
  RightSideWrapper,
  Step,
  StepSubTitle,
  StyledAppIcon,
  BillingDetails,
  MonthlyBillingContainer,
  RecurringPaymentContainer,
  MonthText,
  MonthlyPriceContainer,
  ProductName,
  PackageInfos,
  PackageInfo,
  ImportantNotes,
  ImportantNotesTitle,
  StyledListItem,
  UserDetailsSection,
  LogoutButton,
  UserName,
  StyledList,
  StyledWarningIcon,
  StyledDoneIcon,
  DesktopView,
  MobileView,
};
